import {formatNumber, formatPercent} from '@angular/common';
import {Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {Subscription} from 'rxjs';
import * as echarts from 'echarts';
import {AdminDashboardDataService} from '@features/admin-panel/services/admin-dashboard-data.service';
import {countOf} from '@features/admin-panel/helpers/admin-dashboard-text';
import {adbAlpha, adbChartTheme, adbLegend, adbRedrawOnThemeChange, adbTooltip, adbValueAxis} from '@features/admin-panel/helpers/admin-dashboard-chart-theme';
import {
  AdminDashboardQueryParams,
  AdminDashboardSeatPoint,
  AdminDashboardSeatUtilisation,
  AdminDashboardWidgetState
} from '@features/admin-panel/models/admin-dashboard.models';
import {AdminDashboardReportBlock, AdminDashboardReportProvider} from '@features/admin-panel/models/admin-dashboard-report.models';
import {AdminDashboardReportRegistry} from '@features/admin-panel/services/admin-dashboard-report-registry.service';

/**
 * The ECharts entry point behind a plain object, so a spec can stub it: the `echarts` module namespace is
 * read-only, and `spyOn(echarts, 'init')` throws.
 */
export const seatUtilisationCharts = {
  init: (element: HTMLElement): echarts.ECharts => echarts.init(element)
};

/**
 * Users & adoption "Seat utilisation": licensed seats against the users active in the last 30 days, one dot per
 * paying client, over- and under-use counted. Clients only; pending until the first nightly insights run.
 */
@Component({
  standalone: false,
  selector: 'app-admin-dashboard-seat-utilisation',
  templateUrl: './admin-dashboard-seat-utilisation.component.html',
  styleUrls: ['./admin-dashboard-seat-utilisation.component.scss']
})
export class AdminDashboardSeatUtilisationComponent implements OnChanges, OnInit, OnDestroy, AdminDashboardReportProvider {
  /** Redraws with the new theme's colours when the painted theme flips (GEN-E-137-08). */
  private readonly themeRedraw = adbRedrawOnThemeChange(() => this.render());
  @Input() params: AdminDashboardQueryParams;

  /** A dot was clicked: the dashboard opens that company's why panel (OCA-E-159 DetailSections.tsx:146-155). */
  @Output() companySelected = new EventEmitter<string>();
  /** What the card has to show, reported to the dashboard (ITP-E-173-01). */
  @Output() stateChange = new EventEmitter<AdminDashboardWidgetState>();
  widgetState: AdminDashboardWidgetState = 'loading';

  /** The states' texts, shared by the template and the PDF report. */
  readonly notApplicableText = 'No Client Manager record';
  readonly pendingText = 'Seat use is computed nightly — first results after tonight\'s run';
  readonly emptyText = 'No paying companies with licensed seats in this view';
  readonly failedTitle = 'Couldn\'t load seat utilisation';

  data: AdminDashboardSeatUtilisation | null = null;
  loaded = false;
  /** The last load failed: the card says so instead of looking empty. */
  failed = false;

  private chartElement: HTMLDivElement;
  private chart: echarts.ECharts;
  private resizeObserver: ResizeObserver;
  private loadSubscription: Subscription;

  constructor(private dataService: AdminDashboardDataService, private reportRegistry: AdminDashboardReportRegistry, private host: ElementRef<HTMLElement>) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['params']) {
      this.load();
    }
  }

  ngOnInit(): void {
    this.reportRegistry.register('users-adoption', this.host.nativeElement, this);
  }

  /** The chart container exists only while there are points to draw. */
  @ViewChild('chart') set chartContainer(container: ElementRef<HTMLDivElement> | undefined) {
    if (container?.nativeElement === this.chartElement) {
      return;
    }
    this.disposeChart();
    if (container) {
      this.chartElement = container.nativeElement;
      this.chart = seatUtilisationCharts.init(this.chartElement);
      this.chart.on('click', (event: any) => this.pick(event?.data?.point));
      this.resizeObserver = new ResizeObserver(() => this.chart?.resize());
      this.resizeObserver.observe(this.chartElement);
      this.render();
    }
  }

  ngOnDestroy(): void {
    this.loadSubscription?.unsubscribe();
    this.disposeChart();
    this.reportRegistry.unregister(this);
  }

  /** The card for the PDF report, built from what it shows now; never reloads (ITP-E-146-09). */
  reportBlocks(): AdminDashboardReportBlock[] {
    if (!this.loaded) {
      return [];
    }
    const title = 'Seat utilisation';
    if (this.failed) {
      return [{kind: 'empty', title, text: this.failedTitle}];
    }
    if (this.data?.notApplicable) {
      return [{kind: 'empty', title, text: this.notApplicableText}];
    }
    if (this.data?.pending) {
      return [{kind: 'empty', title, text: this.pendingText}];
    }
    if (!this.hasPoints) {
      return [{kind: 'empty', title, text: this.emptyText}];
    }
    return [{
      kind: 'chart',
      title,
      subtitle: `Active users in the last 30 days against licensed seats. ${this.overText} · ${this.underText}`,
      image: this.chart ? this.chart.getDataURL({type: 'png', pixelRatio: 2, backgroundColor: '#FFFFFF'}) : null,
      widthPx: this.chart?.getWidth() ?? 0,
      heightPx: this.chart?.getHeight() ?? 0
    }];
  }

  get hasPoints(): boolean {
    return !!this.data && !this.data.notApplicable && !this.data.pending && (this.data.points?.length ?? 0) > 0;
  }

  /** Loaded, applicable, not pending, and no paying client with seats. */
  get isEmpty(): boolean {
    return this.loaded && !this.failed && !this.hasPoints && !this.data?.notApplicable && !this.data?.pending;
  }

  /** "1 company over-using → upsell" */
  get overText(): string {
    const count = this.data?.over ?? 0;
    return `${formatNumber(count, 'en-US', '1.0-0')} ${count === 1 ? 'company' : 'companies'} over-using → upsell`;
  }

  /** "2 under 40% → risk" */
  get underText(): string {
    return `${formatNumber(this.data?.under ?? 0, 'en-US', '1.0-0')} under ${formatPercent(this.data?.lowSeatUse ?? 0, 'en-US', '1.0-0')} → risk`;
  }

  /** "Acme Ltd<br/>12 of 10 seats used (120%)" */
  tooltipText(point: AdminDashboardSeatPoint | undefined): string {
    if (!point) {
      return '';
    }
    return `${point.companyName}<br/>${formatNumber(point.used, 'en-US', '1.0-0')} of ${countOf(point.seats, 'seat')} used (${formatPercent(point.ratio, 'en-US', '1.0-0')})`;
  }

  /** The company behind a clicked dot; the silent parity line carries no point and never emits. */
  pick(point: AdminDashboardSeatPoint | undefined): void {
    if (point?.companyId) {
      this.companySelected.emit(point.companyId);
    }
  }

  private load(): void {
    this.loadSubscription?.unsubscribe();
    if (!this.params) {
      return;
    }
    // A reload keeps showing the last result until the new one arrives, so only the first load reports 'loading'.
    if (!this.loaded) {
      this.report('loading');
    }
    this.loadSubscription = this.dataService.getData<AdminDashboardSeatUtilisation>(
      'AdminDashboardSeatUtilisationQuery', this.params, 'SeatUtilisation'
    ).subscribe({
      next: data => this.show(data ?? null, false),
      error: () => this.show(null, true)
    });
  }

  /** No Client Manager record and the pending state have nothing to show either: they report empty. */
  private show(data: AdminDashboardSeatUtilisation | null, failed: boolean): void {
    this.data = data;
    this.failed = failed;
    this.loaded = true;
    this.render();
    this.report(failed ? 'failed' : this.hasPoints ? 'content' : 'empty');
  }

  private report(state: AdminDashboardWidgetState): void {
    this.widgetState = state;
    this.stateChange.emit(state);
  }

  private disposeChart(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.chart?.dispose();
    this.chart = null;
    this.chartElement = null;
  }

  private render(): void {
    if (!this.chart || !this.hasPoints) {
      return;
    }
    const points = this.data.points;
    const max = Math.max(10, ...points.map(p => Math.max(p.seats, p.used)));
    // A log axis cannot show 0, so a client with no active user sits just under 1.
    const group = (cls: string) => points
      .filter(p => p.class === cls)
      .map(p => ({value: [p.seats, Math.max(0.8, p.used)], point: p, symbolSize: 5 + Math.min(14, Math.sqrt(p.seats))}));
    // OCA-E-159 seatOption (charts.ts:259-310): in-range dots first, pale; under-use red and over-use green drawn
    // on top so they are never hidden; legend top right. Colours come from the card's --adb-* tokens.
    const t = adbChartTheme(this.host.nativeElement);
    const int = (value: number): string => formatNumber(value, 'en-US', '1.0-0');
    const axisName = {color: t.ink2, fontSize: 11};
    const option = {
      textStyle: {fontFamily: t.font},
      grid: {left: 44, right: 16, top: 30, bottom: 36},
      legend: {...adbLegend(t), icon: 'circle', itemWidth: 8, itemHeight: 8, data: ['Over-used — upsell', 'Under-used — risk', 'In range']},
      xAxis: adbValueAxis(t, int, {type: 'log', min: 1, max, name: 'Licensed seats', nameLocation: 'middle', nameGap: 24, nameTextStyle: axisName}),
      yAxis: adbValueAxis(t, int, {type: 'log', min: 0.8, max, name: 'Active users, 30 days', nameTextStyle: axisName}),
      tooltip: adbTooltip(t, {trigger: 'item', formatter: (param: any) => this.tooltipText(param?.data?.point)}),
      series: [
        {name: 'Parity', type: 'line', data: [[1, 1], [max, max]], showSymbol: false, silent: true,
         lineStyle: {type: 'dashed', width: 1, color: t.ink2}, tooltip: {show: false}},
        {name: 'In range', type: 'scatter', data: group('normal'), itemStyle: {color: adbAlpha(t.ink2, 0.45)}},
        {name: 'Under-used — risk', type: 'scatter', data: group('under'), itemStyle: {color: t.red, opacity: 0.85}},
        {name: 'Over-used — upsell', type: 'scatter', data: group('over'), itemStyle: {color: t.green, opacity: 0.9}}
      ]
    };
    this.chart.setOption(option, true);
  }
}
